import React, { useEffect, useState } from 'react';
import { getAiUsage } from '../../services/SettingsService';

const PURPOSE_LABELS = {
    SIGNAL: '🎯 Sinal por-ativo',
    MARKET_SCAN: '🔍 Scanner de mercado',
    WEEKLY_EVAL: '📋 Avaliação semanal',
    RISK_REVIEW: '🛡️ Revisão de risco'
};

function usageBar(used, limit) {
    if (!limit) return null;
    const pct = Math.min((used / limit) * 100, 100);
    const cls = pct >= 90 ? 'bg-danger' : pct >= 70 ? 'bg-warning' : 'bg-success';
    return (
        <div className='progress mt-1' style={{ height: 6 }}>
            <div className={`progress-bar ${cls}`} style={{ width: `${pct}%` }} />
        </div>
    );
}

/**
 * props:
 * - monthlyLimit  — valor atual do campo (number | '')
 * - onChange      — handler para atualizar o campo no form pai
 */
function AiUsagePanel(props) {
    const { monthlyLimit, onChange } = props;
    const [usage, setUsage] = useState(null);

    useEffect(() => {
        getAiUsage()
            .then(setUsage)
            .catch(() => {});
    }, []);

    const totalUsed   = usage ? usage.total.inputTokens + usage.total.outputTokens : 0;
    const todayUsed   = usage && usage.today ? usage.today.inputTokens + usage.today.outputTokens : 0;
    const todayCalls  = usage && usage.today ? usage.today.calls : 0;
    const weekUsed    = usage && usage.week  ? usage.week.inputTokens  + usage.week.outputTokens  : 0;
    const weekCalls   = usage && usage.week  ? usage.week.calls : 0;
    const limit       = parseInt(monthlyLimit) || 0;
    const daysInMonth = (usage && usage.daysInMonth) || 30;
    const dailyTarget = limit > 0 ? Math.floor(limit / daysInMonth) : 0;

    return (
        <div className='card border-0 shadow-sm p-3 mb-3'>
            <h6 className='mb-3'>Uso de Tokens da IA</h6>

            {usage ? (
                <>
                    {/* Bloco de HOJE e SEMANA */}
                    <div className='row g-2 mb-3'>
                    <div className='col-md-6'>
                    <div className='border rounded p-2 h-100'>
                        <div className='d-flex justify-content-between align-items-center mb-1'>
                            <strong className='small'>Hoje</strong>
                            <small className='text-muted'>{todayCalls} chamadas</small>
                        </div>
                        {dailyTarget > 0 ? (
                            <>
                                <small className='text-muted'>
                                    {todayUsed.toLocaleString()} / {dailyTarget.toLocaleString()} tokens
                                    {' '}({((todayUsed / dailyTarget) * 100).toFixed(0)}% da média diária esperada)
                                </small>
                                {usageBar(todayUsed, dailyTarget)}
                                <small className='text-muted d-block mt-1' style={{ fontSize: '0.7rem' }}>
                                    Meta diária = limite mensal ÷ {daysInMonth} dias.
                                </small>
                            </>
                        ) : (
                            <small className='text-muted'>
                                {todayUsed.toLocaleString()} tokens consumidos hoje.
                                {' '}<span className='fst-italic'>Defina um limite mensal abaixo para ver a meta diária.</span>
                            </small>
                        )}
                    </div>
                    </div>
                    <div className='col-md-6'>
                    <div className='border rounded p-2 h-100'>
                        <div className='d-flex justify-content-between align-items-center mb-1'>
                            <strong className='small'>Esta semana</strong>
                            <small className='text-muted'>{weekCalls} chamadas</small>
                        </div>
                        <small className='text-muted'>
                            {weekUsed.toLocaleString()} tokens consumidos na semana atual.
                        </small>
                        {limit > 0 && (() => {
                            const weekTarget = Math.floor(limit / (daysInMonth / 7));
                            return weekTarget > 0 ? (
                                <>
                                    {usageBar(weekUsed, weekTarget)}
                                    <small className='text-muted d-block mt-1' style={{ fontSize: '0.7rem' }}>
                                        {weekUsed.toLocaleString()} / {weekTarget.toLocaleString()} (meta semanal estimada)
                                    </small>
                                </>
                            ) : null;
                        })()}
                    </div>
                    </div>
                    </div>

                    {/* Bloco do MÊS */}
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                        <strong className='small'>Mês atual</strong>
                    </div>
                    <div className='d-flex gap-4 mb-2 flex-wrap'>
                        <div className='text-center'>
                            <div className='fs-5 fw-bold'>{usage.total.calls}</div>
                            <small className='text-muted'>Chamadas</small>
                        </div>
                        <div className='text-center'>
                            <div className='fs-5 fw-bold'>{usage.total.inputTokens.toLocaleString()}</div>
                            <small className='text-muted'>Tokens entrada</small>
                        </div>
                        <div className='text-center'>
                            <div className='fs-5 fw-bold'>{usage.total.outputTokens.toLocaleString()}</div>
                            <small className='text-muted'>Tokens saída</small>
                        </div>
                        <div className='text-center'>
                            <div className='fs-5 fw-bold'>{totalUsed.toLocaleString()}</div>
                            <small className='text-muted'>Total</small>
                        </div>
                    </div>

                    {limit > 0 && (
                        <div className='mb-2'>
                            <small className='text-muted'>
                                {totalUsed.toLocaleString()} / {limit.toLocaleString()} tokens ({((totalUsed / limit) * 100).toFixed(1)}%)
                            </small>
                            {usageBar(totalUsed, limit)}
                        </div>
                    )}

                    {usage.dailySeries && usage.dailySeries.some(d => d.tokens > 0) && (() => {
                        const series = usage.dailySeries;
                        const max = Math.max(...series.map(d => d.tokens), 1);
                        const W = 320, H = 60, n = series.length, gap = 1;
                        const bw = (W - gap * (n - 1)) / n;
                        return (
                            <>
                                <div className='text-uppercase fw-bold text-muted mt-3 mb-1' style={{ fontSize: 11, letterSpacing: 0.5 }}>
                                    Evolução (30 dias)
                                </div>
                                <svg viewBox={`0 0 ${W} ${H}`} width='100%' height={H} preserveAspectRatio='none' style={{ display: 'block' }}>
                                    {series.map((d, i) => {
                                        const h = d.tokens > 0 ? Math.max(1, (d.tokens / max) * (H - 2)) : 0;
                                        return (
                                            <rect key={i} x={i * (bw + gap)} y={H - h} width={bw} height={h} fill='#0d6efd'>
                                                <title>{d.day}: {d.tokens.toLocaleString()} tokens · {d.calls} chamadas</title>
                                            </rect>
                                        );
                                    })}
                                </svg>
                                <div className='d-flex justify-content-between'>
                                    <small className='text-muted' style={{ fontSize: 10 }}>{series[0].day}</small>
                                    <small className='text-muted' style={{ fontSize: 10 }}>pico {max.toLocaleString()} tok/dia</small>
                                    <small className='text-muted' style={{ fontSize: 10 }}>hoje</small>
                                </div>
                            </>
                        );
                    })()}

                    {usage.byProvider && usage.byProvider.length > 0 && (
                        <>
                            <div className='text-uppercase fw-bold text-muted mt-3 mb-1' style={{ fontSize: 11, letterSpacing: 0.5 }}>
                                Por provider
                            </div>
                            <table className='table table-sm mb-0'>
                                <thead>
                                    <tr>
                                        <th>Provider</th>
                                        <th className='text-center'>Chamadas</th>
                                        <th className='text-end'>Entrada</th>
                                        <th className='text-end'>Saída</th>
                                        <th className='text-end'>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usage.byProvider.map(r => {
                                        const tot = parseInt(r.inputTokens) + parseInt(r.outputTokens);
                                        return (
                                            <tr key={r.provider}>
                                                <td>{r.provider}</td>
                                                <td className='text-center'>{r.calls}</td>
                                                <td className='text-end'>{parseInt(r.inputTokens).toLocaleString()}</td>
                                                <td className='text-end'>{parseInt(r.outputTokens).toLocaleString()}</td>
                                                <td className='text-end fw-bold'>{tot.toLocaleString()}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}

                    {usage.byPurpose && usage.byPurpose.length > 0 && (
                        <>
                            <div className='text-uppercase fw-bold text-muted mt-3 mb-1' style={{ fontSize: 11, letterSpacing: 0.5 }}>
                                Por finalidade
                            </div>
                            <table className='table table-sm mb-0'>
                                <thead>
                                    <tr>
                                        <th>Finalidade</th>
                                        <th className='text-center'>Chamadas</th>
                                        <th className='text-end'>Tokens</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usage.byPurpose.map(r => (
                                        <tr key={r.purpose}>
                                            <td>{PURPOSE_LABELS[r.purpose] || r.purpose}</td>
                                            <td className='text-center'>{r.calls}</td>
                                            <td className='text-end fw-bold'>{r.tokens.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {usage.byAutomation && usage.byAutomation.length > 0 && (
                        <>
                            <div className='text-uppercase fw-bold text-muted mt-3 mb-1' style={{ fontSize: 11, letterSpacing: 0.5 }}>
                                Por automação (maiores consumidoras)
                            </div>
                            <table className='table table-sm mb-0'>
                                <thead>
                                    <tr>
                                        <th>Automação</th>
                                        <th className='text-center'>Chamadas</th>
                                        <th className='text-end'>Tokens</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usage.byAutomation.slice(0, 10).map(r => (
                                        <tr key={r.automationId}>
                                            <td>{r.name}</td>
                                            <td className='text-center'>{r.calls}</td>
                                            <td className='text-end fw-bold'>{r.tokens.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </>
            ) : (
                <small className='text-muted'>Carregando...</small>
            )}

            {!props.readOnly && (
                <div className='mt-3'>
                    <label className='form-label small'>Teto global de emergência <span className='text-muted'>(0 = sem limite)</span></label>
                    <input
                        type='number'
                        className='form-control form-control-sm'
                        style={{ maxWidth: 180 }}
                        min='0'
                        step='10000'
                        value={monthlyLimit ?? ''}
                        onChange={onChange}
                        id='aiMonthlyTokenLimit'
                        name='aiMonthlyTokenLimit'
                    />
                    <small className='text-muted'>
                        Bloqueia <strong>todos</strong> os providers quando o total combinado é atingido.
                        Diferente do limite por provider (que apenas aciona o próximo fallback), este para toda a IA.
                    </small>
                </div>
            )}
        </div>
    );
}

export default AiUsagePanel;
